import { cn } from "@tine/designsystem-utils";
import { getYear, isToday, parseISO } from "date-fns";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useInView } from "react-intersection-observer";
import { useLocation } from "wouter";

import { sendSelectTeaser, sendViewTeaser } from "../../../common/tracking";
import type { ContentfulAdventCalendarSlot } from "../../../common/types/cmsTypes";
import { isDateInTheFuture, isDateInThePast } from "../../../common/utils/dateUtils";

import contentStore from "../../../stores/cms/contentStore";
import promotionStore from "../../../stores/promotionStore";
import { getAdventCalendarSignUpCode } from "./adventCalendarUtils";

type AdventCalendarSlotProps = {
   content: ContentfulAdventCalendarSlot;
   trackingListName?: string;
};

const SLOT_COMMON_STYLES = {
   base: "w-full rounded-lg",
   behindSlotImage: "absolute left-0 top-0 -z-10 h-full object-cover visible"
};

const AdventCalendarSlot = ({ content, trackingListName }: AdventCalendarSlotProps) => {
   const [_, redirectTo] = useLocation();
   const [shouldShakeSlot, setShouldShakeSlot] = useState(false);
   const [isOpen, setIsOpen] = useState(false);

   const date = parseISO(content.fields.date || "");
   const slotId = content.sys.id;
   const url = `/${contentStore.urlData.data.find((urlData) => urlData.id === content.sys.id)?.fullUrl}`;

   const slotImage = content.fields.slotImage;
   const behindSlotImage = content.fields.behindSlotImage;
   const contentType = "adventCalendarSlot";

   const signUpCode = getAdventCalendarSignUpCode(content);
   const trackingId = `julekalender-${getYear(date)}-${content.fields.slug}`;
   const isSignedUp = promotionStore.isSignedUpTo(signUpCode);
   const isSlotOpenedOrOutdated = isSignedUp || isDateInThePast(date);

   function goToCalendarSlot() {
      if (isSlotOpenedOrOutdated) {
         redirectTo(url);
      } else {
         // Wait for the animation to finish before redirecting
         setTimeout(() => {
            redirectTo(url);
         }, 700);
      }
   }

   function shakeCalendarSlot() {
      setShouldShakeSlot(true);
      setTimeout(() => {
         setShouldShakeSlot(false);
      }, 500);
   }

   function handleClickImageLink() {
      if (!isDateInTheFuture(date)) {
         contentStore.fetchContentIfNotAlreadyInStore(slotId, "adventCalendarSlot");
         sendSelectTeaser(trackingId, contentType, trackingListName, url);
         setIsOpen(true);
         goToCalendarSlot();
      } else {
         shakeCalendarSlot();
      }
   }

   const trackImpression = (isVisible: boolean) => {
      if (isVisible) {
         sendViewTeaser(trackingId, contentType, trackingListName);
      }
   };

   const { ref: viewRef } = useInView({
      triggerOnce: true,
      onChange: trackImpression
   });

   return (
      <>
         {slotImage && (
            // biome-ignore lint/a11y/noStaticElementInteractions: should be a button
            // biome-ignore lint/a11y/useAriaPropsSupportedByRole: should be a button
            <div
               ref={viewRef}
               onClick={() => handleClickImageLink()}
               className={cn(SLOT_COMMON_STYLES.base, "relative h-auto hover:cursor-pointer", {
                  "[&>img:first-child]:hover:hover-calendar-slot": isSlotOpenedOrOutdated
               })}
               aria-expanded={isOpen}
               aria-label={`Kalenderluke ${content.fields.date?.slice(-2)} ${isSlotOpenedOrOutdated ? "åpen" : "lukket"} `}
            >
               <img
                  alt=""
                  className={cn(
                     SLOT_COMMON_STYLES.base,
                     "z-0 block aspect-square h-auto origin-left overflow-hidden object-cover transition-transform duration-500 ease-[cubic-bezier(0.42,0,0.58,1)]",
                     {
                        "dsp-shake-calendar-slot": shouldShakeSlot,
                        "opened-calendar-slot": isSlotOpenedOrOutdated,
                        "open-calendar-slot": isOpen && isToday(date) && !isSignedUp
                     }
                  )}
                  src={slotImage.fields.file?.url}
               />
               {behindSlotImage?.fields.file ? (
                  <img
                     alt=""
                     className={cn(SLOT_COMMON_STYLES.base, SLOT_COMMON_STYLES.behindSlotImage, "brightness-50", {
                        "hidden opacity-0": !isOpen && !isSlotOpenedOrOutdated && !isToday(date)
                     })}
                     src={behindSlotImage.fields.file?.url}
                  />
               ) : (
                  <div
                     className={cn(SLOT_COMMON_STYLES, SLOT_COMMON_STYLES.behindSlotImage, "bg-ink-brand-subtle", {
                        "hidden opacity-0": !isOpen && !isSlotOpenedOrOutdated && !isToday(date)
                     })}
                  />
               )}
            </div>
         )}
      </>
   );
};

export default observer(AdventCalendarSlot);
